import SupplierLogoWithFallback from 'elements/SupplierLogoWithFallback'
import { MenuItem, Select, styled, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import useEnabledHardwareSuppliers from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { fromDisplaySourceValue, SELECTABLE_MANUAL_SOURCES, toDisplaySourceValue } from 'pages/ordering/utils'
import { memo, useMemo } from 'react'

const MANUAL_SOURCE_KEYS: Record<typeof SELECTABLE_MANUAL_SOURCES[number], string> = {
  exclude: 'Exclude from Order',
  '3rd_party': 'Purchased from 3rd Party',
  stock_on_hand: 'Stock on Hand',
}

const StyledSelect = styled(Select)({
  minWidth: 180,
})

const ManufacturerContainer = styled('div')({
  display: 'inline-flex',
  alignContent: 'center',
  alignItems: 'center',
  padding: '4px',
})

const ManufacturerName = styled(Typography)({
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
})

const ManufacturerImgContainer = styled('div')(({ theme }) => ({
  width: 34,
  height: 20,
  marginRight: 8,
  border: `1px solid ${theme.palette.neutralGrey[500]}`,
  borderRadius: 4,
  padding: '4px',
}))

type SourceSelectorProps = {
  value: HardwareSupplierFilterKeyType | 'component' | undefined
  onChange: (source: HardwareSupplierFilterKeyType | undefined) => void
  /** List of distributors that have ordering data for this component */
  availableDistributors?: HardwareSupplierFilterKeyType[]
  disabled?: boolean
  /** When true, hide this distributor from the dropdown when it should not be user-selectable */
  hideDistributor?: (d: HardwareSupplierFilterKeyType) => boolean
}

const SourceSelector = memo(
  ({ value, onChange, availableDistributors, disabled, hideDistributor }: SourceSelectorProps) => {
    const translate = useTranslate()
    const { getSupplierConfig } = useGetSupplierConfig()
    const { filteredSuppliers } = useEnabledHardwareSuppliers()

    // Only show distributors that are both enabled AND have ordering data for this component
    const filteredDistributors = useMemo(() => {
      let list =
        availableDistributors === undefined
          ? filteredSuppliers
          : filteredSuppliers.filter((distributor) => availableDistributors.includes(distributor))
      if (hideDistributor) {
        list = list.filter((d) => !hideDistributor(d))
      }
      return list
    }, [filteredSuppliers, availableDistributors, hideDistributor])

    const displayValue = toDisplaySourceValue(value)

    const handleSelectChange = (e: React.ChangeEvent<{ value: unknown }>) => {
      const raw = fromDisplaySourceValue(e.target.value as string)
      onChange(raw as HardwareSupplierFilterKeyType | undefined)
    }

    const renderValue = (selected: unknown) => {
      const selectedValue = selected as HardwareSupplierFilterKeyType
      if (!selectedValue || selectedValue === 'default') {
        return (
          <ManufacturerContainer>
            <ManufacturerName>{translate('Select Source')}</ManufacturerName>
          </ManufacturerContainer>
        )
      }
      const manualKey = SELECTABLE_MANUAL_SOURCES.includes(selectedValue as typeof SELECTABLE_MANUAL_SOURCES[number])
        ? MANUAL_SOURCE_KEYS[selectedValue as typeof SELECTABLE_MANUAL_SOURCES[number]]
        : null
      if (manualKey) {
        return (
          <ManufacturerContainer>
            <ManufacturerName>{translate(manualKey)}</ManufacturerName>
          </ManufacturerContainer>
        )
      }
      const supplierConfig = getSupplierConfig(selectedValue)
      if (supplierConfig) {
        return (
          <ManufacturerContainer>
            <ManufacturerImgContainer>
              <SupplierLogoWithFallback logoUrl={supplierConfig.logoUrl} name={supplierConfig.name} />
            </ManufacturerImgContainer>
            <ManufacturerName>{supplierConfig.name}</ManufacturerName>
          </ManufacturerContainer>
        )
      }
      // Fallback when config is missing (e.g. distributor key from API doesn't match org config): show the selected value so the selection is visible
      return (
        <ManufacturerContainer>
          <ManufacturerName>{selectedValue}</ManufacturerName>
        </ManufacturerContainer>
      )
    }

    return (
      <StyledSelect
        value={displayValue}
        onChange={handleSelectChange}
        displayEmpty
        renderValue={renderValue}
        disabled={disabled}
      >
        <MenuItem disabled value="default">
          <ManufacturerContainer>
            <ManufacturerName>{translate('Select Source')}</ManufacturerName>
          </ManufacturerContainer>
        </MenuItem>
        {filteredDistributors.map((distributor) => {
          const supplierConfig = getSupplierConfig(distributor)
          if (!supplierConfig) return null

          return (
            <MenuItem key={distributor} value={distributor}>
              <ManufacturerContainer>
                <ManufacturerImgContainer>
                  <SupplierLogoWithFallback logoUrl={supplierConfig.logoUrl} name={supplierConfig.name} />
                </ManufacturerImgContainer>
                <ManufacturerName>{supplierConfig.name}</ManufacturerName>
              </ManufacturerContainer>
            </MenuItem>
          )
        })}
        {SELECTABLE_MANUAL_SOURCES.map((source) => (
          <MenuItem key={source} value={source}>
            <ManufacturerContainer>
              <ManufacturerName>{translate(MANUAL_SOURCE_KEYS[source])}</ManufacturerName>
            </ManufacturerContainer>
          </MenuItem>
        ))}
      </StyledSelect>
    )
  }
)

export default SourceSelector
